/**
 * 좌표 변환 유틸리티
 * WGS-84 (GPS 원본) <-> GCJ-02 (중국 국가표준) <-> BD-09 (바이두)
 */

const PI = Math.PI
const X_PI = (PI * 3000.0) / 180.0
const A = 6378245.0 // 장반경
const EE = 0.00669342162296594323 // 이심률 제곱

function transformLat(x: number, y: number): number {
    let ret = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x))
    ret += ((20.0 * Math.sin(6.0 * x * PI) + 20.0 * Math.sin(2.0 * x * PI)) * 2.0) / 3.0
    ret += ((20.0 * Math.sin(y * PI) + 40.0 * Math.sin((y / 3.0) * PI)) * 2.0) / 3.0
    ret += ((160.0 * Math.sin((y / 12.0) * PI) + 320 * Math.sin((y * PI) / 30.0)) * 2.0) / 3.0
    return ret
}

function transformLng(x: number, y: number): number {
    let ret = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x))
    ret += ((20.0 * Math.sin(6.0 * x * PI) + 20.0 * Math.sin(2.0 * x * PI)) * 2.0) / 3.0
    ret += ((20.0 * Math.sin(x * PI) + 40.0 * Math.sin((x / 3.0) * PI)) * 2.0) / 3.0
    ret += ((150.0 * Math.sin((x / 12.0) * PI) + 300.0 * Math.sin((x / 30.0) * PI)) * 2.0) / 3.0
    return ret
}

function outOfChina(lat: number, lng: number): boolean {
    return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271
}

/** WGS-84 -> GCJ-02 */
export function wgs84ToGcj02(lat: number, lng: number): [number, number] {
    if (outOfChina(lat, lng)) return [lat, lng]

    let dLat = transformLat(lng - 105.0, lat - 35.0)
    let dLng = transformLng(lng - 105.0, lat - 35.0)
    const radLat = (lat / 180.0) * PI
    let magic = Math.sin(radLat)
    magic = 1 - EE * magic * magic
    const sqrtMagic = Math.sqrt(magic)
    dLat = (dLat * 180.0) / (((A * (1 - EE)) / (magic * sqrtMagic)) * PI)
    dLng = (dLng * 180.0) / ((A / sqrtMagic) * Math.cos(radLat) * PI)
    return [lat + dLat, lng + dLng]
}

/** GCJ-02 -> BD-09 */
export function gcj02ToBd09(lat: number, lng: number): [number, number] {
    const z = Math.sqrt(lng * lng + lat * lat) + 0.00002 * Math.sin(lat * X_PI)
    const theta = Math.atan2(lat, lng) + 0.000003 * Math.cos(lng * X_PI)
    const bdLng = z * Math.cos(theta) + 0.0065
    const bdLat = z * Math.sin(theta) + 0.006
    return [bdLat, bdLng]
}

/** WGS-84 -> BD-09 (GPS 원본 좌표를 바이두 좌표로 변환) */
export function wgs84ToBd09(lat: number, lng: number): [number, number] {
    const [gcjLat, gcjLng] = wgs84ToGcj02(lat, lng)
    return gcj02ToBd09(gcjLat, gcjLng)
}

/** BD-09 -> GCJ-02 */
export function bd09ToGcj02(lat: number, lng: number): [number, number] {
    const x = lng - 0.0065
    const y = lat - 0.006
    const z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * X_PI)
    const theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * X_PI)
    const gcjLng = z * Math.cos(theta)
    const gcjLat = z * Math.sin(theta)
    return [gcjLat, gcjLng]
}

/** GCJ-02 -> WGS-84 (근사값) */
export function gcj02ToWgs84(lat: number, lng: number): [number, number] {
    if (outOfChina(lat, lng)) return [lat, lng]
    const [gcjLat, gcjLng] = wgs84ToGcj02(lat, lng)
    const dLat = gcjLat - lat
    const dLng = gcjLng - lng
    return [lat - dLat, lng - dLng]
}

/** BD-09 -> WGS-84 */
export function bd09ToWgs84(lat: number, lng: number): [number, number] {
    const [gcjLat, gcjLng] = bd09ToGcj02(lat, lng)
    return gcj02ToWgs84(gcjLat, gcjLng)
}
